import { useState, useContext } from "react";
import {
  Box,
  TextField,
  Button,
  Alert,
  Typography,
  CircularProgress,
  Link,
  InputAdornment,
  Paper,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Fade,
  LinearProgress,
  Chip,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useFormik } from "formik";
import { AuthContext } from "../../contexts/AuthContext";
import changePasswordValidationSchema from "../../utils/validation-schema/changePasswordValidation";
import { useNavigate } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import SecurityIcon from "@mui/icons-material/Security";
import PasswordIcon from "@mui/icons-material/Password";
import EmailIcon from "@mui/icons-material/Email";

const ChangePasswordTab = ({ email }) => {
  const { changePassword } = useContext(AuthContext);
  const [statusMsg, setStatusMsg] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const navigate = useNavigate();

  const passwordRequirements = [
    { label: "At least 8 characters long", regex: /.{8,}/, weight: 1 },
    {
      label: "Contains at least one uppercase letter",
      regex: /[A-Z]/,
      weight: 1,
    },
    {
      label: "Contains at least one lowercase letter",
      regex: /[a-z]/,
      weight: 1,
    },
    { label: "Contains at least one number", regex: /\d/, weight: 1 },
    {
      label: "Contains at least one special character",
      regex: /[!@#$%^&*(),.?":{}|<>]/,
      weight: 1,
    },
  ];

  const formik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: changePasswordValidationSchema,
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);
      setStatusMsg({ type: "", message: "" });

      try {
        const { currentPassword, newPassword } = values;
        await changePassword({ currentPassword, newPassword });
        setStatusMsg({
          type: "success",
          message:
            "Password updated successfully! Your new password is now active.",
        });
        resetForm();
        setShowPasswords({ current: false, new: false, confirm: false });
      } catch (error) {
        setStatusMsg({
          type: "error",
          message:
            error?.response?.data?.message ||
            "Failed to update password. Please try again.",
        });
      } finally {
        setLoading(false);
      }
    },
  });

  const checkPasswordRequirement = (requirement) => {
    return requirement.regex.test(formik.values.newPassword);
  };

  const calculatePasswordStrength = () => {
    if (!formik.values.newPassword) return 0;
    const metRequirements = passwordRequirements.filter(
      checkPasswordRequirement
    );
    return (metRequirements.length / passwordRequirements.length) * 100;
  };

  const getPasswordStrengthColor = (strength) => {
    if (strength < 40) return "error";
    if (strength < 80) return "warning";
    return "success";
  };

  const getPasswordStrengthLabel = (strength) => {
    if (strength < 40) return "Weak";
    if (strength < 80) return "Fair";
    return "Strong";
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const passwordStrength = calculatePasswordStrength();
  const strengthColor = getPasswordStrengthColor(passwordStrength);
  const strengthLabel = getPasswordStrengthLabel(passwordStrength);

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", px: 3, py: 5 }}>
      <Stack spacing={4}>
        {/* Header Section */}
        <Box>
          <Stack direction='row' spacing={2} alignItems='center' mb={2}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 56,
                height: 56,
                borderRadius: "50%",
                bgcolor: "primary.lighter",
                color: "primary.main",
              }}
            >
              <SecurityIcon fontSize='large' />
            </Box>
            <Box>
              <Typography variant='h4' fontWeight='bold' gutterBottom>
                Security Settings
              </Typography>
              <Typography variant='body1' color='text.secondary'>
                Manage your account security and password
              </Typography>
            </Box>
          </Stack>

          {/* User Info Card */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: "info.lighter",
              border: "1px solid",
              borderColor: "secondary.main",
            }}
          >
            <Stack direction='row' spacing={2} alignItems='center'>
              <EmailIcon color='secondary' />
              <Box>
                <Typography variant='body2' color='text.secondary' gutterBottom>
                  Currently logged in as
                </Typography>
                <Typography
                  variant='body1'
                  fontWeight={600}
                  color='secondary.dark'
                >
                  {email}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Box>

        {statusMsg.message && (
          <Fade in={Boolean(statusMsg.message)}>
            <Alert
              severity={statusMsg.type}
              sx={{
                borderRadius: 2,
                "& .MuiAlert-message": { fontSize: "1rem" },
              }}
            >
              {statusMsg.message}
            </Alert>
          </Fade>
        )}

        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Typography
            variant='h6'
            sx={{
              fontWeight: 600,
              mb: 3,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <PasswordIcon color='primary' />
            Change Password
          </Typography>

          <form onSubmit={formik.handleSubmit}>
            <Stack spacing={3}>
              {/* Current Password */}
              <TextField
                label='Current Password'
                name='currentPassword'
                type={showPasswords.current ? "text" : "password"}
                fullWidth
                value={formik.values.currentPassword}
                onChange={formik.handleChange}
                error={
                  formik.touched.currentPassword &&
                  Boolean(formik.errors.currentPassword)
                }
                helperText={
                  formik.touched.currentPassword &&
                  formik.errors.currentPassword
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <Stack direction='row' spacing={1} alignItems='center'>
                        <Tooltip
                          title={
                            showPasswords.current
                              ? "Hide password"
                              : "Show password"
                          }
                        >
                          <IconButton
                            onClick={() => togglePasswordVisibility("current")}
                            edge='end'
                            size='small'
                          >
                            {showPasswords.current ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Link
                          component='button'
                          variant='body2'
                          onClick={() =>
                            navigate(`/password-reset?email=${email}`)
                          }
                          sx={{
                            whiteSpace: "nowrap",
                            textDecoration: "none",
                            color: "primary.main",
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          }}
                        >
                          Forgot password?
                        </Link>
                      </Stack>
                    </InputAdornment>
                  ),
                }}
              />

              <Divider />

              {/* New Password */}
              <TextField
                label='New Password'
                name='newPassword'
                type={showPasswords.new ? "text" : "password"}
                fullWidth
                value={formik.values.newPassword}
                onChange={formik.handleChange}
                error={
                  formik.touched.newPassword &&
                  Boolean(formik.errors.newPassword)
                }
                helperText={
                  formik.touched.newPassword && formik.errors.newPassword
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <Tooltip
                        title={
                          showPasswords.new ? "Hide password" : "Show password"
                        }
                      >
                        <IconButton
                          onClick={() => togglePasswordVisibility("new")}
                          edge='end'
                          size='small'
                        >
                          {showPasswords.new ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Password Strength Indicator */}
              {formik.values.newPassword && (
                <Box>
                  <Stack
                    direction='row'
                    justifyContent='space-between'
                    alignItems='center'
                    mb={1}
                  >
                    <Typography variant='body2' color='text.secondary'>
                      Password Strength
                    </Typography>
                    <Chip
                      label={strengthLabel}
                      color={strengthColor}
                      size='small'
                      variant='outlined'
                    />
                  </Stack>
                  <LinearProgress
                    variant='determinate'
                    value={passwordStrength}
                    color={strengthColor}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>
              )}

              {/* Confirm Password */}
              <TextField
                label='Confirm New Password'
                name='confirmPassword'
                type={showPasswords.confirm ? "text" : "password"}
                fullWidth
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                error={
                  formik.touched.confirmPassword &&
                  Boolean(formik.errors.confirmPassword)
                }
                helperText={
                  formik.touched.confirmPassword &&
                  formik.errors.confirmPassword
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <Tooltip
                        title={
                          showPasswords.confirm
                            ? "Hide password"
                            : "Show password"
                        }
                      >
                        <IconButton
                          onClick={() => togglePasswordVisibility("confirm")}
                          edge='end'
                          size='small'
                        >
                          {showPasswords.confirm ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Password Requirements */}
              {formik.values.newPassword && (
                <Paper
                  variant='outlined'
                  sx={{
                    p: 3,
                    bgcolor: "grey.50",
                    borderRadius: 2,
                    borderColor: "grey.200",
                  }}
                >
                  <Typography
                    variant='subtitle1'
                    color='text.primary'
                    fontWeight={600}
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <LockIcon fontSize='small' />
                    Password Requirements
                  </Typography>
                  <List dense disablePadding>
                    {passwordRequirements.map((req, index) => (
                      <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {checkPasswordRequirement(req) ? (
                            <CheckCircleOutlineIcon
                              color='success'
                              fontSize='small'
                            />
                          ) : (
                            <ErrorOutlineIcon color='error' fontSize='small' />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={req.label}
                          primaryTypographyProps={{
                            variant: "body2",
                            color: checkPasswordRequirement(req)
                              ? "text.primary"
                              : "text.secondary",
                            fontWeight: checkPasswordRequirement(req)
                              ? 500
                              : 400,
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}

              <Button
                variant='contained'
                color='primary'
                type='submit'
                fullWidth
                sx={{
                  mt: 2,
                  height: 52,
                  position: "relative",
                  fontWeight: 600,
                  fontSize: "1rem",
                  borderRadius: 2,
                  textTransform: "none",
                }}
                disabled={loading || !formik.isValid}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : (
                  "Update Password"
                )}
              </Button>
            </Stack>
          </form>
        </Paper>
      </Stack>
    </Box>
  );
};

export default ChangePasswordTab;
